import { cn } from '@/lib/utils';

interface TableSkeletonProps {
  /** Number of placeholder rows. Defaults to 5 — enough to look populated without overshooting common page sizes. */
  rows?: number;
  /** Column widths as Tailwind width utilities. Length is the column count. */
  cols: string[];
  /** Optional className passed through to the wrapping <table>. */
  className?: string;
}

/**
 * Drop-in skeleton table — renders its own <table>/<tbody> chrome so the
 * caller can swap it for the loading spinner without restructuring the
 * conditional:
 *
 *   {isLoading ? (
 *     <TableSkeleton cols={['w-32','w-20','w-16','w-24','w-16']} />
 *   ) : empty ? (
 *     <EmptyState ... />
 *   ) : (
 *     <table>...real rows...</table>
 *   )}
 *
 * Each cell renders a pulsing rounded bar at the requested width. Tailwind's
 * `animate-pulse` already honors prefers-reduced-motion; the explicit
 * `motion-reduce:animate-none` is a belt-and-suspenders guard for the
 * project's reduced-motion contract.
 */
export function TableSkeleton({ rows = 5, cols, className }: TableSkeletonProps): JSX.Element {
  return (
    <table
      aria-busy="true"
      aria-live="polite"
      className={cn('w-full text-sm', className)}
    >
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} className="border-t">
            {cols.map((w, c) => (
              <td key={c} className="py-2">
                <div
                  className={cn(
                    'h-3 max-w-full animate-pulse rounded bg-muted-foreground/15 motion-reduce:animate-none',
                    w,
                  )}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
